import React from 'react';
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LandingPage from './pages/LandingPage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { ClientLayout, ClientDashboard, DossiersList, RemindersList, InvoicesList } from './pages/ClientDashboard';
import { ArchitectLayout, ArchitectDashboard, ArchitectDossiersList, ArchitectDossierDetail } from './pages/ArchitectDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="text-center">
          <p className="font-bold text-2xl tracking-tighter font-['Outfit'] mb-4">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </p>
          <p className="text-[#52525B]">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'architecte') return <Navigate to="/architect" replace />;
    return <Navigate to="/client" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <p className="text-[#52525B]">Chargement...</p>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'architecte') return <Navigate to="/architect" replace />;
    return <Navigate to="/client" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      
      {/* Client Routes */}
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client', 'admin']}><ClientLayout /></ProtectedRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="dossiers" element={<DossiersList />} />
        <Route path="reminders" element={<RemindersList />} />
        <Route path="invoices" element={<InvoicesList />} />
      </Route>
      
      {/* Architect Routes */}
      <Route path="/architect" element={<ProtectedRoute allowedRoles={['architecte', 'admin']}><ArchitectLayout /></ProtectedRoute>}>
        <Route index element={<ArchitectDashboard />} />
        <Route path="dossiers" element={<ArchitectDossiersList />} />
        <Route path="dossiers/:id" element={<ArchitectDossierDetail />} />
      </Route>
      
      {/* Admin redirects to client for now */}
      <Route path="/admin" element={<Navigate to="/client" replace />} />
      <Route path="/admin/*" element={<Navigate to="/client" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                borderRadius: '0',
                border: '1px solid #E5E7EB',
              },
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
